import { useState } from "react";
import { styled } from "styled-components";
import { createLinkProps } from "../../hooks/useLinkProps.js";
import { HeaderMenuItem, MultiLevelHeaderMenuItem } from "../../types/theme.js";
import { minus, plus } from "../../icons/index.js";
import { createLabel } from "../../util/createLabel.js";

interface Props {
  menu: MultiLevelHeaderMenuItem[] | HeaderMenuItem[];
}

export const MobileNavigation: React.FC<Props> = ({ menu }) => {
  const level = 0;
  const [openSubMenus, setOpenSubMenus] = useState<{ [key: number]: boolean }>(
    {}
  );

  const toggleSubMenu = (menuItemIndex: number) => {
    setOpenSubMenus((prev) => ({
      ...prev,
      [menuItemIndex]: !prev[menuItemIndex], // Toggle individual submenu
    }));
  };

  return (
    <Menu className={`top-level-menu mobile`}>
      {menu.map((menuItem, menuItemIndex) => {
        const hasSubMenu =
          "subMenu" in menuItem &&
          menuItem.subMenu &&
          menuItem.subMenu.length > 0;
        const isOpen = openSubMenus[menuItemIndex] || false;

        return (
          <li key={menuItemIndex}>
            <a
              className={isOpen ? "active" : ""}
              onClick={(e) => {
                if ("label" in menuItem) {
                  e.preventDefault();
                  toggleSubMenu(menuItemIndex);
                }
              }}
              {...(menuItem.link
                ? { ...createLinkProps(menuItem.link) }
                : { href: "#" })}
            >
              {createLabel(menuItem)}{" "}
              {hasSubMenu && <>{isOpen ? minus : plus}</>}
            </a>
            {hasSubMenu && isOpen && (
              <SubMenu menuItem={menuItem} level={level} />
            )}
          </li>
        );
      })}
    </Menu>
  );
};

interface SubMenuProps {
  menuItem: MultiLevelHeaderMenuItem;
  level: number;
}

const SubMenu: React.FC<SubMenuProps> = ({ menuItem, level }) => {
  return (
    <Menu className={`sub-menu sub-menu-${level + 1}`}>
      {menuItem.subMenu?.map((subMenuItem, subMenuItemIndex) => {
        const hasSubMenu =
          subMenuItem.subMenu && subMenuItem.subMenu.length > 0;

        return (
          <li
            key={subMenuItemIndex}
            className={hasSubMenu ? "has-sub-menu" : ""}
          >
            <a
              {...(subMenuItem.link
                ? { ...createLinkProps(subMenuItem.link) }
                : { href: "#" })}
            >
              {createLabel(subMenuItem)}{" "}
            </a>

            {hasSubMenu && (
              <div className="sub-menu-container">
                <SubMenu menuItem={subMenuItem} level={level + 1} />
              </div>
            )}
          </li>
        );
      })}
    </Menu>
  );
};

const Menu = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
  z-index: 10;

  li {
    a {
      box-sizing: border-box;
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: inherit;
      text-decoration: none;
      padding: ${(props) =>
        `${props.theme.spacing.xs} ${props.theme.spacing.l}`};

      &:hover,
      &:focus {
        background-color: var(--swatch4);
        color: var(--swatch2);
      }

      svg {
        path {
          stroke: var(--swatch2);
        }
      }
    }
  }

  &.sub-menu {
    box-sizing: border-box;

    li {
      width: 100%;
    }
  }

  &.sub-menu-1 li a {
    padding-left: calc(${(props) => props.theme.spacing.l} + 20px);
  }

  &.sub-menu-2 li a {
    padding-left: calc(${(props) => props.theme.spacing.l} + 40px);
  }

  &.sub-menu-3 li a {
    padding-left: calc(${(props) => props.theme.spacing.l} + 60px);
  }

  &.sub-menu-4 li a {
    padding-left: calc(${(props) => props.theme.spacing.l} + 80px);
  }
`;
